import {
  useCreateOverlayMutation,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import { LiveAppIllustration } from '@carboncopyco/illustrations';
import type { DemoOverlay } from '@carboncopyco/types';
import {
  Loading as LoadingSpinner,
  TextCaption,
  TextPageHeader,
  TextSubHeader,
  notify,
} from '@carboncopyco/ui';
import {
  convertToProtocolUrl,
  isAllowedHost,
  validateUrl,
} from '@carboncopyco/utils';
import classnames from 'classnames';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';

type ErrorsObject = Record<'url', string>;

type OverlayNavigatorProps = {
  organizationId: string;
  workspaceId: string;
  goToEditor: (demo: DemoOverlay, url: string) => void;
};

function isURL(str: string) {
  return /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/.test(str);
}

export const OverlayNavigator = ({
  organizationId,
  workspaceId,
  goToEditor,
}: OverlayNavigatorProps) => {
  const { folderId } = useParams<{ folderId: string }>();
  const { data: organization } = useGetOrganizationQuery({
    id: organizationId,
  });
  const [createDemoOverlay, { isLoading }] = useCreateOverlayMutation();

  const onSubmit = (url: string) => {
    if (validateUrl(url)) {
      const protocolUrl = convertToProtocolUrl(url);
      const { hostname } = new URL(protocolUrl);

      createDemoOverlay({
        organizationId,
        firstUrl: protocolUrl,
        workspaceId,
        demoName: `Demo for ${hostname}`,
        folderId,
      })
        .unwrap()
        .then((demo) => {
          goToEditor(demo, protocolUrl);
        })
        .catch(() =>
          notify.error(
            'Something went wrong. Please try again in a few seconds.'
          )
        );
    }
  };

  return (
    <div className="flex flex-col items-center justify-center text-center h-full w-full max-w-[468px]">
      <LiveAppIllustration
        height={100}
        width={148}
      />
      <div className="pt-10">
        <TextPageHeader label="Mask a live environment with edits" />
      </div>
      <div className="pt-2">
        <TextSubHeader label="Type your app's URL to add an overlay of edits on top" />
      </div>
      <div className="pt-6 w-full">
        <div className="relative flex items-center">
          <Formik
            initialValues={{ url: '' }}
            validateOnChange={false}
            onSubmit={(values, actions) => {
              actions.setFieldValue('url', convertToProtocolUrl(values.url));
              onSubmit(values.url);
            }}
            validate={(values) => {
              const errors = {} as ErrorsObject;
              if (!values.url) {
                errors.url = 'URL cannot be empty.';
                return errors;
              }
              if (
                organization &&
                !isAllowedHost(values.url, organization.allowedHosts)
              ) {
                errors.url =
                  'Please use a URL of an approved app or demo environment. Contact your account manager to record more apps';
                return errors;
              }
              if (!isURL(convertToProtocolUrl(values.url))) {
                errors.url =
                  'This URL isn’t valid. Make sure you typed the correct URL.';
                return errors;
              }
              return errors;
            }}
          >
            {({ handleSubmit, errors, touched }) => {
              return (
                <Form
                  className="flex flex-col w-full"
                  onSubmit={handleSubmit}
                >
                  <Field
                    name="url"
                    placeholder="Type URL here"
                    autoFocus
                    className={classnames(
                      'rounded-3xl border border-ds-natural-150 pl-5 pr-20 w-full h-12 text-sm focus:outline-none focus:border-ds-primary focus:ring-0 placeholder-ds-natural-300',
                      {
                        'border-ds-danger-400 focus:border-ds-danger-400':
                          errors.url && touched.url,
                      }
                    )}
                    autoComplete="off"
                  />

                  <div className="min-h-ds-6">
                    <ErrorMessage name="url">
                      {(msg) => (
                        <div className="text-left pl-3">
                          <TextCaption
                            label={msg}
                            color="text-ds-danger-400"
                          />
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                  <button
                    type="submit"
                    className="absolute right-2 top-1.5 rounded-3xl bg-ds-primary-300 text-ds-accent3-100 px-5 py-1.5 h-9"
                  >
                    {isLoading ? (
                      <LoadingSpinner color="text-ds-accent3-100" />
                    ) : (
                      'Go'
                    )}
                  </button>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};
